// @flow

import * as React from "react";
import { EXCLUDED_BARCODE_VALUES } from "../../../helpers/excludedBarcodeValues";
import {
  compose,
  setDisplayName,
  withHandlers,
  withStateHandlers
} from "recompose";
import { markInventoryOrderCartonAsReceivedAndLocated, recordStockContainerLocation } from "../../../graph";
import { playAudioForSuccess } from "../../../helpers/playAudioForSuccess";
import { withApollo } from "@apollo/client/react/hoc";
import { withRouter } from "found";
import BarcodeReader from "react-barcode-reader";
import PendingStatusView from "../../../components/PendingStatusView";
import TabbedAppBar from "../../../components/TabbedAppBar";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@mui/material/Typography";
import withSnackbar from "../../../components/withSnackbar";

import type { HOC } from "recompose";

type State = {|
  currentLocation: string,
  isLoading: boolean
|};

const enhancer: HOC<*, *> = compose(
  setDisplayName("ReceiveAndLocateApp"),

  withApollo,

  withRouter,

  withSnackbar,

  markInventoryOrderCartonAsReceivedAndLocated,

  recordStockContainerLocation,

  withStateHandlers(
    ({
      completedScanError: null,
      completedScanData: null,
      currentLocation: null,
      isLoading: false
    }: State),
    {
      setIsLoading: () => (isLoading: boolean) => ({ isLoading }),
      setCurrentLocation: () => (currentLocation: string) => ({
        currentLocation
      }),
      setCompletedScanData: () => (completedScanData: any) => ({
        completedScanData
      }),
      setCompletedScanError: () => (completedScanError: string) => ({
        completedScanError
      })
    }
  ),

  withHandlers({
    handleAppHeaderRequestBack:
      ({ router, backUrl }) =>
      () => {
        router.push(backUrl ? backUrl : "/apps");
      },

    handleBarcodeError:
      ({ showErrorSnackbar, setIsLoading }) =>
      () => {
        showErrorSnackbar("Error scanning barcode");
        setIsLoading(false);
      },

    handleBarcodeScan:
      ({
        markInventoryOrderCartonAsReceivedAndLocated,
        recordStockContainerLocation,
        setIsLoading,
        showErrorSnackbar,
        showSuccessSnackbar,
        setCurrentLocation,
        currentLocation,
        setCompletedScanData,
        setCompletedScanError,
        completedScanData
      }) =>
      barcode => {
        (async props => {
          const {
            barcode, // eslint-disable-next-line no-unused-vars
            markInventoryOrderCartonAsReceivedAndLocated, // eslint-disable-next-line no-unused-vars
            recordStockContainerLocation, // eslint-disable-next-line no-unused-vars
            setIsLoading,
            showErrorSnackbar,
            showSuccessSnackbar,
            setCurrentLocation,
            currentLocation,
            setCompletedScanData
          } = props;

          console.log("ReceiveAndLocateApp Barcode Scan", barcode);

          // reset ui
          setCompletedScanData(null);
          setCompletedScanError(null);

          try {
            if (!barcode || barcode.length === 0) {
              showErrorSnackbar("Invalid Barcode Scanned");
              return;
            }

            // Check if barcode is in the exclusion list
            if (EXCLUDED_BARCODE_VALUES.includes(barcode)) {
              showErrorSnackbar(
                "This barcode is excluded from processing"
              );
              return;
            }

            let isJSON = false;
            let json = null;
            try {
              json = JSON.parse(barcode);
              // Only treat as JSON if it's an object or array, not primitives like null, numbers, booleans, or strings
              isJSON = json !== null && typeof json === 'object';
            } catch (e) {
              isJSON = false;
            }

            if (isJSON && json?.variant === "LOCATION" && (json?.type || "").toLowerCase() === "box" && json?.name) {
              // location barcode scanned
              setCurrentLocation(json.name);
              return;
            }

            if (!currentLocation || currentLocation.length === 0) {
              showErrorSnackbar("Invalid Location Barcode Scanned");
              return;
            }

            if (isJSON) {
              if (json?.variant === "OBJECT" && (json?.type || "").toLowerCase() === "stockcontainer" && json?.id) {
                // stock container barcode scanned - call alternate mutation to relocate stock container
                setIsLoading(true);

                let { data } = await recordStockContainerLocation({
                  variables: {
                    location: currentLocation,
                    stockContainerId: json.id
                  }
                });
                if (data.recordStockContainerLocation.succeeded) {
                  setCompletedScanData({
                    location: currentLocation,
                    cartonIdentifier: json.id,
                    orderNumber: data.recordStockContainerLocation.updatedStockContainer.orderProductionJob.order.orderNumber,
                    poNumber: data.recordStockContainerLocation.updatedStockContainer.orderProductionJob.label,
                    express: data.recordStockContainerLocation.updatedStockContainer.orderProductionJob.order.express,
                    printMethods: data.recordStockContainerLocation.updatedStockContainer.orderProductionJob.customizationAreas.map(area => area.method).join(", ")
                  });
                  showSuccessSnackbar("Box location has been recorded.");
                  playAudioForSuccess();
                } else {
                  let message = data.recordStockContainerLocation.errors.stockContainerId.join(", ");
                  showErrorSnackbar(message);
                  setCompletedScanError("ERROR: " + message);
                }
              }
            } else {
              setIsLoading(true);

              let succeeded = false;

              const handleErrors = (status, errors) => {
                console.log("errors"); // eslint-disable-line no-console
                console.log(errors); // eslint-disable-line no-console

                // display error output
                switch (status) {
                  case "INVENTORY_ORDER_CANCELED":
                    setCompletedScanError("RETURN: Order Canceled");
                    showErrorSnackbar("RETURN: Order Canceled!");
                    return;
                  case "INVENTORY_ORDER_VENDOR_ASSIGNED":
                    setCompletedScanError("RETURN: Order Vendor Assigned");
                    showErrorSnackbar("RETURN: Order Vendor Assigned!");
                    return;
                  case "COMPOSITE_CARTON_UNSUPPORTED":
                    setCompletedScanError("BULK: Deliver to bulk desk");
                    showErrorSnackbar("BULK: Deliver to bulk desk");
                    return;
                  case "INVENTORY_ORDER_CARTON_NOT_FOUND":
                    setCompletedScanError("ERROR: Carton Not Found");
                    showErrorSnackbar("ERROR: Carton Not Found. Receive manually.");
                    return;
                  default:
                    showErrorSnackbar("Unhandled error");
                    break;
                }
              };

              let { data } = await markInventoryOrderCartonAsReceivedAndLocated({
                variables: {
                  location: currentLocation,
                  inventoryOrderCartonIdentifier: barcode
                }
              });
              succeeded = data.markInventoryOrderCartonAsReceivedAndLocated.succeeded;
              if (succeeded) {
                setCompletedScanData({
                  location: currentLocation,
                  cartonIdentifier: barcode,
                  orderNumber: data.markInventoryOrderCartonAsReceivedAndLocated.updatedInventoryOrderCartons[0].orderProductionJob.order.orderNumber,
                  poNumber: data.markInventoryOrderCartonAsReceivedAndLocated.updatedInventoryOrderCartons[0].inventoryOrderItem.inventoryOrder.poNumber,
                  express: data.markInventoryOrderCartonAsReceivedAndLocated.updatedInventoryOrderCartons[0].orderProductionJob.order.express,
                  printMethods: data.markInventoryOrderCartonAsReceivedAndLocated.updatedInventoryOrderCartons[0].orderProductionJob.customizationAreas.map(area => area.method).join(", ")
                });
                showSuccessSnackbar("Box location has been recorded.");
                playAudioForSuccess();
              } else {
                return handleErrors(
                  data.markInventoryOrderCartonAsReceivedAndLocated.errorStatus,
                  data.markInventoryOrderCartonAsReceivedAndLocated.errors
                );
              }
            }
          } catch (xxx) {
            console.log(xxx); // eslint-disable-line no-console
            showErrorSnackbar("Error Scanning Barcode");
          } finally {
            setIsLoading(false);
          }
        })({
          barcode,
          markInventoryOrderCartonAsReceivedAndLocated,
          recordStockContainerLocation,
          setIsLoading,
          showErrorSnackbar,
          showSuccessSnackbar,
          setCurrentLocation,
          currentLocation,
          setCompletedScanData,
          setCompletedScanError,
          completedScanData
        });
      }
  })
);

const ReceiveAndLocateApp = ({
  appBarBackgroundColor,
  currentLocation,
  completedScanData,
  completedScanError,
  handleAppHeaderRequestBack,
  handleBarcodeError,
  handleBarcodeScan,
  isLoading
}) => (
  <div>
    <TabbedAppBar
      title="Receive and Locate"
      onRequestBack={handleAppHeaderRequestBack}
      appBarBackgroundColor={appBarBackgroundColor}
    />
    {isLoading ? (
      <PendingStatusView status="Loading" />
    ) : (
      <div>
        <BarcodeReader
          onError={handleBarcodeError}
          onScan={handleBarcodeScan}
        />
        {currentLocation && (
          <div>
            {completedScanData && (
              <div style={{ marginTop: "2em", textAlign: "center", border: "1px solid #ccc", maxWidth: "400px", marginLeft: "auto", marginRight: "auto" }}>
                <Typography
                  variant="h6"
                  align="center"
                  color="inherit"
                  style={{ flexGrow: 1, marginTop: "1em" }}
                >
                  Last Received Carton Details
                </Typography>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell align="right">
                        Carton
                      </TableCell>
                      <TableCell align="left">
                        {completedScanData.cartonIdentifier}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right">
                        Location
                      </TableCell>
                      <TableCell align="left">
                        {completedScanData.location}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right">
                        Order
                      </TableCell>
                      <TableCell align="left">
                        {completedScanData.orderNumber}
                      </TableCell>
                    </TableRow>
                    {completedScanData.express && (
                      <TableRow>
                        <TableCell align="right">
                          Express
                        </TableCell>
                        <TableCell align="left">
                          <span style={{ fontWeight: "bold", color: "red" }}>EXPRESS</span>
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell align="right">
                        PO
                      </TableCell>
                      <TableCell align="left">
                        {completedScanData.poNumber}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right">
                        Print Methods
                      </TableCell>
                      <TableCell align="left">
                        {completedScanData.printMethods || "N/A"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
            {completedScanError && (
              <Typography
                variant="h6"
                align="center"
                color="error"
                style={{ flexGrow: 1, marginTop: "10em" }}
              >
                {completedScanError}
              </Typography>
            )}
            <Typography
              variant="h6"
              align="center"
              color="inherit"
              style={{ flexGrow: 1, marginTop: "10em" }}
            >
              Current Location: {currentLocation}
              <br />
              Scan Carton
            </Typography>
          </div>
        )}
        {!currentLocation && (
          <Typography
            variant="h6"
            align="center"
            color="inherit"
            style={{ flexGrow: 1, marginTop: "10em" }}
          >
            Scan Location
          </Typography>
        )}
      </div>
    )}
  </div>
);

export default enhancer(ReceiveAndLocateApp);
